import { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, RotateCw, Trash2, Webhook } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Panel } from "@/components/bits";
import { publicApi } from "@/services/api-client";
import {
  ConfirmDialog,
  Empty,
  ErrorBox,
  KV,
  Pager,
  SecretOnce,
  SkeletonRows,
  Tag,
  fmtWhen,
  pageInfo,
  pick,
  record,
  rows,
  safeUrl,
  text,
  useServer,
  type ApiRecord,
} from "@/features/finance/kit";

const EVENTS = [
  "payment.completed",
  "payment.failed",
  "withdrawal.completed",
  "withdrawal.failed",
  "refund.completed",
  "transfer.completed",
  "kyb.approved",
];

export function WebhooksPanel({ applicationId }: { applicationId: string }) {
  const list = useServer<unknown>("/webhooks");
  const items = useMemo(
    () =>
      rows(list.data).filter(
        (w) =>
          !text(w, "application_id") ||
          text(w, "application_id") === applicationId,
      ),
    [list.data, applicationId],
  );
  const [edit, setEdit] = useState<ApiRecord | "new" | null>(null);
  const [view, setView] = useState<string | null>(null);
  const [secret, setSecret] = useState<string>("");

  return (
    <Panel
      title="Webhooks"
      sub="Notifications signées vers vos serveurs (HTTPS uniquement)."
      action={
        <Button
          size="sm"
          onClick={() => setEdit("new")}
          data-testid="button-new-webhook"
        >
          <Plus className="mr-1 h-4 w-4" />
          Ajouter
        </Button>
      }
    >
      {secret && (
        <div className="mb-3">
          <SecretOnce
            label="Secret de signature"
            value={secret}
            onClear={() => setSecret("")}
          />
          <Button
            size="sm"
            variant="ghost"
            className="mt-1"
            onClick={() => setSecret("")}
          >
            J'ai enregistré le secret
          </Button>
        </div>
      )}
      {list.error ? (
        <ErrorBox error={list.error} onRetry={list.reload} />
      ) : list.loading ? (
        <SkeletonRows n={2} />
      ) : items.length === 0 ? (
        <Empty icon={<Webhook className="h-5 w-5" />} title="Aucun webhook" />
      ) : (
        <ul className="divide-y rounded-lg border">
          {items.map((w) => (
            <li
              key={text(w, "id")}
              className="flex flex-wrap items-center gap-2 px-3 py-2.5"
            >
              <button
                className="min-w-0 flex-1 text-left"
                onClick={() => setView(text(w, "id"))}
              >
                <p className="truncate font-mono text-xs">{text(w, "url")}</p>
                <p className="truncate text-[11px] text-muted-foreground">
                  {Array.isArray(w.events)
                    ? (w.events as unknown[]).join(", ")
                    : ""}
                </p>
              </button>
              <Tag value={text(w, "environment")} />
              <Tag value={text(w, "status")} />
              <Button
                size="sm"
                variant="outline"
                className="h-8"
                onClick={() => setEdit(w)}
              >
                Modifier
              </Button>
            </li>
          ))}
        </ul>
      )}
      <WebhookForm
        value={edit}
        applicationId={applicationId}
        onClose={() => setEdit(null)}
        onSaved={(s) => {
          if (s) setSecret(s);
          list.reload();
        }}
      />
      <Dialog open={Boolean(view)} onOpenChange={(o) => !o && setView(null)}>
        <DialogContent className="max-h-[92dvh] overflow-y-auto sm:max-w-[600px]">
          {view && (
            <WebhookDetail
              id={view}
              onSecret={setSecret}
              onDeleted={() => {
                setView(null);
                list.reload();
              }}
            />
          )}
        </DialogContent>
      </Dialog>
    </Panel>
  );
}

function WebhookForm({
  value,
  applicationId,
  onClose,
  onSaved,
}: {
  value: ApiRecord | "new" | null;
  applicationId: string;
  onClose: () => void;
  onSaved: (secret?: string) => void;
}) {
  const isNew = value === "new";
  const [url, setUrl] = useState("");
  const [events, setEvents] = useState<string[]>([]);
  const [env, setEnv] = useState("SANDBOX");
  const [status, setStatus] = useState("ACTIVE");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  useEffect(() => {
    if (!value) return;
    const w = value === "new" ? {} : value;
    setUrl(text(w, "url"));
    setEvents(
      Array.isArray(w.events)
        ? (w.events as unknown[]).map(String)
        : ["payment.completed"],
    );
    setEnv(text(w, "environment") || "SANDBOX");
    setStatus(text(w, "status") || "ACTIVE");
    setErr(null);
  }, [value]);
  const valid = safeUrl(url)?.startsWith("https://") && events.length > 0;
  async function save() {
    setBusy(true);
    setErr(null);
    try {
      if (isNew) {
        const { data } = await publicApi<ApiRecord>("/webhooks", {
          method: "POST",
          body: {
            application_id: applicationId,
            url: url.trim(),
            events,
            environment: env,
          },
        });
        onSaved(
          text(
            data,
            "secret",
            "signing_secret",
            "webhook.secret",
            "webhook.signing_secret",
          ),
        );
      } else if (value) {
        await publicApi(`/webhooks/${encodeURIComponent(text(value, "id"))}`, {
          method: "PUT",
          body: { url: url.trim(), events, status, environment: env },
        });
        onSaved();
      }
      onClose();
    } catch (x) {
      setErr(x);
    } finally {
      setBusy(false);
    }
  }
  const allEvents = Array.from(new Set([...EVENTS, ...events]));
  return (
    <Dialog
      open={Boolean(value)}
      onOpenChange={(o) => !o && !busy && onClose()}
    >
      <DialogContent className="sm:max-w-[480px]">
        <DialogHeader>
          <DialogTitle>
            {isNew ? "Nouveau webhook" : "Modifier le webhook"}
          </DialogTitle>
          <DialogDescription>L'URL doit être en HTTPS.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div className="grid gap-1.5">
            <Label htmlFor="wh-url">URL</Label>
            <Input
              id="wh-url"
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://marchand.example/webhooks/bizconnectpay"
              aria-invalid={Boolean(url) && !valid}
              data-testid="input-webhook-url"
            />
          </div>
          <fieldset className="grid gap-1.5">
            <legend className="mb-1 text-sm font-medium">Événements</legend>
            <div className="flex flex-wrap gap-1.5">
              {allEvents.map((e) => {
                const on = events.includes(e);
                return (
                  <button
                    type="button"
                    key={e}
                    aria-pressed={on}
                    onClick={() =>
                      setEvents((c) =>
                        on ? c.filter((x) => x !== e) : [...c, e],
                      )
                    }
                    className={`rounded-full border px-2.5 py-1 font-mono text-[11px] ${on ? "border-primary bg-primary text-primary-foreground" : "bg-card"}`}
                  >
                    {e}
                  </button>
                );
              })}
            </div>
          </fieldset>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="wh-env">Environnement</Label>
              <Select value={env} onValueChange={setEnv}>
                <SelectTrigger id="wh-env">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="SANDBOX">SANDBOX</SelectItem>
                  <SelectItem value="LIVE">LIVE</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {!isNew && (
              <div className="grid gap-1.5">
                <Label htmlFor="wh-st">Statut</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger id="wh-st">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ACTIVE">ACTIVE</SelectItem>
                    <SelectItem value="DISABLED">DISABLED</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <ErrorBox error={err} />
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>
            Annuler
          </Button>
          <Button
            onClick={() => void save()}
            disabled={!valid || busy}
            data-testid="button-save-webhook"
          >
            {busy && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Enregistrer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function WebhookDetail({
  id,
  onSecret,
  onDeleted,
}: {
  id: string;
  onSecret: (s: string) => void;
  onDeleted: () => void;
}) {
  const res = useServer<unknown>(`/webhooks/${encodeURIComponent(id)}`);
  const [page, setPage] = useState(1);
  const deliveries = useServer<unknown>(
    `/webhooks/${encodeURIComponent(id)}/deliveries?per_page=25&page=${page}`,
  );
  const dl = useMemo(() => rows(deliveries.data), [deliveries.data]);
  const info = pageInfo(deliveries.data, deliveries.meta, dl.length, 25);
  const [confirm, setConfirm] = useState<"rotate" | "delete" | null>(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<unknown>(null);
  async function act() {
    setBusy(true);
    setErr(null);
    try {
      if (confirm === "rotate") {
        const { data } = await publicApi<ApiRecord>(
          `/webhooks/${encodeURIComponent(id)}/rotate-secret`,
          { method: "POST", body: {} },
        );
        onSecret(text(data, "secret", "signing_secret", "webhook.secret"));
        setConfirm(null);
      } else {
        await publicApi(`/webhooks/${encodeURIComponent(id)}`, {
          method: "DELETE",
        });
        setConfirm(null);
        onDeleted();
      }
    } catch (x) {
      setErr(x);
    } finally {
      setBusy(false);
    }
  }
  return (
    <>
      <DialogHeader>
        <DialogTitle>Webhook</DialogTitle>
        <DialogDescription className="font-mono">{id}</DialogDescription>
      </DialogHeader>
      {res.error ? (
        <ErrorBox error={res.error} onRetry={res.reload} />
      ) : res.loading ? (
        <SkeletonRows n={2} />
      ) : (
        <KV data={record(record(res.data).webhook ?? res.data)} />
      )}
      <div className="flex gap-2">
        <Button
          size="sm"
          variant="outline"
          onClick={() => {
            setErr(null);
            setConfirm("rotate");
          }}
        >
          <RotateCw className="mr-1 h-4 w-4" />
          Renouveler le secret
        </Button>
        <Button
          size="sm"
          variant="outline"
          className="text-destructive"
          onClick={() => {
            setErr(null);
            setConfirm("delete");
          }}
        >
          <Trash2 className="mr-1 h-4 w-4" />
          Supprimer
        </Button>
      </div>
      <h4 className="mt-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
        Livraisons
      </h4>
      <div className="overflow-hidden rounded-lg border">
        {deliveries.error ? (
          <ErrorBox
            className="m-2"
            error={deliveries.error}
            onRetry={deliveries.reload}
          />
        ) : deliveries.loading ? (
          <SkeletonRows n={2} />
        ) : dl.length === 0 ? (
          <p className="p-4 text-center text-xs text-muted-foreground">
            Aucune livraison.
          </p>
        ) : (
          <ul className="divide-y text-xs">
            {dl.map((x, i) => (
              <li
                key={text(x, "id") || i}
                className="flex flex-wrap items-center gap-2 px-3 py-2"
              >
                <span className="font-mono">
                  {text(x, "event", "event_type")}
                </span>
                <span className="text-muted-foreground">
                  {fmtWhen(pick(x, "created_at", "attempted_at"))}
                </span>
                <span className="ml-auto font-mono">
                  {text(x, "response_status", "http_status")}
                </span>
                <Tag value={text(x, "status")} />
              </li>
            ))}
          </ul>
        )}
        {(dl.length > 0 || page > 1) && (
          <Pager info={info} loading={deliveries.loading} onPage={setPage} />
        )}
      </div>
      <ConfirmDialog
        open={Boolean(confirm)}
        destructive
        title={
          confirm === "rotate"
            ? "Renouveler le secret ?"
            : "Supprimer ce webhook ?"
        }
        description={
          confirm === "rotate"
            ? "L'ancien secret cessera de signer les envois. Le nouveau ne sera affiché qu'une fois."
            : "Les événements ne seront plus envoyés à cette URL."
        }
        busy={busy}
        error={err}
        onClose={() => setConfirm(null)}
        onConfirm={() => void act()}
      />
    </>
  );
}
